/**
 * UserAnalysisReport — admin usage-metrics dashboard tab (soda-5675,
 * InstantReplayCDK/specs/open/user-analysis-report.md §5), rendered as the
 * "User Analysis Report" tab inside AdminPanel. No longer owns its own
 * overlay/close chrome -- AdminPanel does -- this component is just the
 * tab's content, matching FeedbackManagement.tsx's and
 * ConversationLookup.tsx's existing pattern.
 *
 * Three parts, top to bottom: the same 7/14/30/60d sinceDays control as
 * FeedbackManagement.tsx, `summary`'s five at-a-glance numbers, then
 * per-metric `dailyTrend` line charts (via `recharts`, already a
 * dependency -- see ChartBlock.tsx for this codebase's existing usage
 * pattern) and a `topUsers` ranked list.
 *
 * `avgLatencySeconds`/`retentionFromPriorDay` are `number | null` per day
 * (§4, Decisions Log #6) -- a `null` day must render as a real gap in its
 * line chart, not a `0`-valued point, since a fabricated `0` would
 * misrepresent "no data that day" as "instant responses"/"nobody
 * retained." `recharts`' `Line` already skips `null`/`undefined` data
 * points by default (does not draw through them) as long as
 * `connectNulls` is left unset -- confirmed by reading `recharts`'
 * behavior, not assumed; this component relies on that default rather
 * than pre-filling `null` with a placeholder value.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { fetchUserAnalysisReport, UserAnalysisReport as UserAnalysisReportData, VALID_SINCE_DAYS, SinceDays } from './fetchUserAnalysisReport';
import { ThemeColors } from './useTheme';

interface UserAnalysisReportProps {
  colors: ThemeColors;
}

type LoadState = 'loading' | 'loaded' | 'error';

const SINCE_DAYS_LABELS: Record<SinceDays, string> = {
  7: '7d',
  14: '14d',
  30: '30d',
  60: '60d',
};

/** Formats a UTC YYYY-MM-DD date string for compact display on a chart's x-axis. */
function formatChartDate(dateUtc: string): string {
  const date = new Date(`${dateUtc}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateUtc;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Rounds a fractional metric (e.g. avgQuestionsPerConversation, a retention ratio) to 2 decimal places for display. */
function roundForDisplay(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Formats a latency in seconds to 1 decimal place for display -- seconds values are small enough (typically sub-10s) that whole-number rounding (the old ms convention) would lose meaningful precision. */
function formatLatencySeconds(seconds: number): string {
  return `${seconds.toFixed(1)} s`;
}

interface TrendChartProps {
  colors: ThemeColors;
  title: string;
  data: UserAnalysisReportData['dailyTrend'];
  dataKey: 'dau' | 'conversationCount' | 'totalQuestions' | 'avgLatencySeconds' | 'retentionFromPriorDay';
  valueFormatter?: (value: number) => string;
}

/** One metric's trend-line chart, shared by every row in the dailyTrend section below. */
function TrendChart({ colors, title, data, dataKey, valueFormatter }: TrendChartProps): React.ReactElement {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: colors.text }}>{title}</div>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
          <XAxis dataKey="date" tickFormatter={formatChartDate} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip
            labelFormatter={(label: string) => formatChartDate(label)}
            formatter={(value: number | string) => (typeof value === 'number' && valueFormatter ? valueFormatter(value) : value)}
          />
          {/* connectNulls intentionally left unset (defaults to false) -- a
              null day (avgLatencySeconds/retentionFromPriorDay with no data)
              must render as a real gap, not be bridged over as if the
              missing day didn't exist (see this file's own header comment). */}
          <Line type="monotone" dataKey={dataKey} stroke={colors.suggestionText} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function UserAnalysisReport({ colors }: UserAnalysisReportProps): React.ReactElement {
  const [sinceDays, setSinceDays] = useState<SinceDays>(7);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [report, setReport] = useState<UserAnalysisReportData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadReport = useCallback((days: SinceDays) => {
    setLoadState('loading');
    setLoadError(null);
    fetchUserAnalysisReport(days)
      .then((result) => {
        setReport(result);
        setLoadState('loaded');
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : 'Unable to load usage report');
        setLoadState('error');
      });
  }, []);

  useEffect(() => {
    loadReport(sinceDays);
  }, [sinceDays, loadReport]);

  return (
    <div style={{ padding: '16px 20px' }}>
      {/* Time-range control -- same pattern as FeedbackManagement.tsx */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {VALID_SINCE_DAYS.map((days) => (
          <button
            key={days}
            onClick={() => setSinceDays(days)}
            style={{
              padding: '6px 14px',
              border: `1px solid ${sinceDays === days ? colors.suggestionText : colors.border}`,
              borderRadius: '16px',
              background: sinceDays === days ? colors.suggestionBg : 'transparent',
              color: sinceDays === days ? colors.suggestionText : colors.textMuted,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: sinceDays === days ? 600 : 400,
            }}
          >
            {SINCE_DAYS_LABELS[days]}
          </button>
        ))}
      </div>

      {loadState === 'loading' && <div style={{ color: colors.textMuted, padding: '20px 0' }}>Loading usage report...</div>}

      {loadState === 'error' && (
        <div style={{ padding: '20px 0' }}>
          <div style={{ color: '#f44336', marginBottom: '8px' }}>Unable to load usage report: {loadError}</div>
          <button
            onClick={() => loadReport(sinceDays)}
            style={{ padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: '13px' }}
          >
            Retry
          </button>
        </div>
      )}

      {loadState === 'loaded' && report && (
        <>
          {/* summary's five at-a-glance numbers */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>{roundForDisplay(report.summary.dau)}</div>
              <div style={{ fontSize: '11px', color: colors.textMuted }}>Avg. daily active users</div>
            </div>
            <div style={{ flex: '1 1 140px', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>{report.summary.conversationCount}</div>
              <div style={{ fontSize: '11px', color: colors.textMuted }}>Conversations</div>
            </div>
            <div style={{ flex: '1 1 140px', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>{report.summary.totalQuestions}</div>
              <div style={{ fontSize: '11px', color: colors.textMuted }}>Total questions</div>
            </div>
            <div style={{ flex: '1 1 140px', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>{roundForDisplay(report.summary.avgQuestionsPerConversation)}</div>
              <div style={{ fontSize: '11px', color: colors.textMuted }}>Avg. questions / conversation</div>
            </div>
            <div style={{ flex: '1 1 140px', border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>{formatLatencySeconds(report.summary.avgLatencySeconds)}</div>
              <div style={{ fontSize: '11px', color: colors.textMuted }}>Avg. latency</div>
            </div>
          </div>

          {/* dailyTrend charts, one per metric */}
          <TrendChart colors={colors} title="Daily active users" data={report.dailyTrend} dataKey="dau" />
          <TrendChart colors={colors} title="Conversations per day" data={report.dailyTrend} dataKey="conversationCount" />
          <TrendChart colors={colors} title="Questions per day" data={report.dailyTrend} dataKey="totalQuestions" />
          <TrendChart
            colors={colors}
            title="Average latency (s)"
            data={report.dailyTrend}
            dataKey="avgLatencySeconds"
            valueFormatter={formatLatencySeconds}
          />
          <TrendChart
            colors={colors}
            title="Day-over-day retention"
            data={report.dailyTrend}
            dataKey="retentionFromPriorDay"
            valueFormatter={(v) => `${Math.round(v * 100)}%`}
          />

          {/* topUsers ranked list */}
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', marginTop: '8px' }}>Top users</div>
          {report.topUsers.length === 0 ? (
            <div style={{ color: colors.textMuted, padding: '12px 0' }}>No activity in this window.</div>
          ) : (
            <div style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', overflow: 'hidden' }}>
              {report.topUsers.map((user, index) => (
                <div
                  key={user.userAlias}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderBottom: index < report.topUsers.length - 1 ? `1px solid ${colors.border}` : 'none',
                    fontSize: '13px',
                  }}
                >
                  <span>
                    <span style={{ color: colors.textMuted, marginRight: '8px' }}>{index + 1}.</span>
                    {user.userAlias}
                  </span>
                  <span style={{ color: colors.textMuted }}>{user.questionCount} question{user.questionCount === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
